import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../utils/supabase';
import { Order } from '../types';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, getOrderStatusTranslation } from '../utils/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 12;
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);

  useEffect(() => {
    fetchOrders();
    
    const subscription = supabase
      .channel('orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
        fetchOrders();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from('habib_orders')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });
    if (data) setOrders(data);
  };

  const totalPages = Math.ceil(orders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const currentOrders = orders.slice(startIndex, startIndex + ordersPerPage);

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-xl font-bold" style={{color: '#05472A'}}>{t('myOrders')}</h1>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <button onClick={() => navigate('/')} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('backToProducts')}
              </button>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-6" style={{borderColor: '#05472A20'}}>
          <h2 className="text-2xl font-bold mb-6" style={{color: '#05472A'}}>{t('orderHistory')}</h2>
          
          {orders.length === 0 ? (
            <p style={{color: '#05472A99'}}>{t('noOrdersYet2')}</p>
          ) : (
            <>
              <div className="space-y-4">
                {currentOrders.map((order) => (
                  <div key={order.id} className="p-4 rounded-lg" style={{backgroundColor: '#05472A10'}}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold" style={{color: '#05472A'}}>{t('order')} #{order.id}</p>
                        <p style={{color: '#05472A99'}}>{t('totalItems2')}: {order.total_items}</p>
                        <p className="text-sm" style={{color: '#05472A99'}}>
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        {order.note && (
                          <div className="mt-2 p-2 bg-white rounded-lg border" style={{borderColor: '#05472A20'}}>
                            <p className="text-xs font-semibold mb-1" style={{color: '#05472A'}}>Note:</p>
                            <p className="text-sm" style={{color: '#05472A99'}}>{order.note}</p>
                          </div>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.status === 'Pesanan Selesai' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getOrderStatusTranslation(order.status, language)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-6 flex justify-between items-center">
                  <p className="text-sm" style={{color: '#05472A99'}}>
                    Showing {startIndex + 1}-{Math.min(startIndex + ordersPerPage, orders.length)} of {orders.length}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{borderColor: '#05472A40', color: '#05472A'}}
                    >
                      ←
                    </button>
                    <span className="px-3 py-1 font-medium" style={{color: '#05472A'}}>
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{borderColor: '#05472A40', color: '#05472A'}}
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Orders;
